/**
 * Community Utils
 * 커뮤니티 공통 유틸리티 함수
 */

/**
 * 날짜 포맷팅 (한국 시간대)
 * @param {string} dateString - ISO 날짜 문자열
 * @returns {string} 포맷된 날짜 문자열
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  // SQLite datetime('now')는 UTC지만 'Z' 접미사가 없음 → 명시적으로 추가
  const normalized = dateString.includes('T') || dateString.endsWith('Z')
    ? dateString
    : dateString.replace(' ', 'T') + 'Z';
  const date = new Date(normalized);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Seoul'
  });
};

/**
 * 날짜만 포맷팅 (한국 시간대)
 * @param {string} dateString - ISO 날짜 문자열
 * @returns {string} 포맷된 날짜 문자열
 */
export const formatDateOnly = (dateString) => {
  if (!dateString) return '';
  const normalized = dateString.includes('T') || dateString.endsWith('Z')
    ? dateString
    : dateString.replace(' ', 'T') + 'Z';
  const date = new Date(normalized);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: 'Asia/Seoul'
  });
};

/**
 * UTC 날짜 문자열 정규화
 * SQLite datetime("now") -> "YYYY-MM-DD HH:MM:SS" (UTC이지만 Z 없음)
 * 이 함수로 Z 접미사를 추가하여 new Date()가 올바르게 UTC로 파싱하도록 함
 * @param {string} dateString - 날짜 문자열
 * @returns {string} 정규화된 날짜 문자열
 */
export const normalizeUtcDate = (dateString) => {
  if (!dateString) return dateString;
  if (dateString.includes("T") || dateString.endsWith("Z")) return dateString;
  return dateString.replace(" ", "T") + "Z";
};

/**
 * 상대 시간 포맷팅 (n분 전, n시간 전 등)
 * @param {string} dateString - ISO 날짜 문자열
 * @returns {string} 상대 시간 문자열
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return "";
  const normalized = normalizeUtcDate(dateString);
  const date = new Date(normalized);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;
  return date.toLocaleDateString("ko-KR", {
    year: "numeric", month: "numeric", day: "numeric",
    timeZone: "Asia/Seoul"
  });
};

/**
 * 긴 날짜 포맷팅 (요일 포함)
 * @param {string} dateString - ISO 날짜 문자열
 * @returns {string} 포맷된 날짜 문자열
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return "";
  const normalized = normalizeUtcDate(dateString);
  const date = new Date(normalized);
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
    timeZone: "Asia/Seoul"
  });
};

/**
 * 날짜만 포맷팅 (요일 포함, 짧은 형식)
 * @param {string} dateString - ISO 날짜 문자열
 * @returns {string} 포맷된 날짜 문자열
 */
export const formatDateWithWeekday = (dateString) => {
  if (!dateString) return "";
  const normalized = normalizeUtcDate(dateString);
  const date = new Date(normalized);
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
    timeZone: "Asia/Seoul"
  });
};

/**
 * 짧은 날짜 포맷팅 (탭/리스트용)
 * @param {string} dateString - ISO 날짜 문자열
 * @returns {string} 포맷된 날짜 문자열
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return "";
  const normalized = normalizeUtcDate(dateString);
  const date = new Date(normalized);
  return date.toLocaleDateString("ko-KR", {
    month: "short",
    day: "numeric",
    weekday: "short",
    timeZone: "Asia/Seoul"
  });
};

/**
 * 뉴스용 상대 시간 (최근은 상대시간, 오래된 것은 짧은 날짜)
 * @param {string} dateString - ISO 날짜 문자열
 * @returns {string} 포맷된 시간 문자열
 */
export const formatNewsTime = (dateString) => {
  if (!dateString) return "";
  const normalized = normalizeUtcDate(dateString);
  const date = new Date(normalized);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  return date.toLocaleDateString("ko-KR", { month: "short", day: "numeric", timeZone: "Asia/Seoul" });
};

/**
 * 토큰 가져오기
 * @returns {string} JWT 토큰
 */
export const getToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'ilouli_token') {
      return decodeURIComponent(value);
    }
  }
  return localStorage.getItem('ilouli_token_backup') || '';
};
